import React, { useEffect, useRef } from 'react';
import TopModal from '@common_components/modals/topModal.component';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  noConfirmationModal?: boolean;
  icon?: React.ReactNode;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}


const BaseModal: React.FC<BaseModalProps> = ( {
  isOpen,
  onClose,
  icon,
  title,
  children,
  footer,
  className = '',
  noConfirmationModal = false,
} ) => {
  const modalRef = useRef<HTMLDivElement>( null );

  useEffect( () => {
    document.body.style.overflow = 'hidden';
  }, [] );

  useEffect( () => {
    if ( !isOpen ) {
      return;
    }
    const handleKeyDown = ( e: KeyboardEvent ) => {
      if ( e.key === 'Escape' ) {
        onClose();
      }
    };
    window.addEventListener( 'keydown', handleKeyDown );
    return () => {
      window.removeEventListener( 'keydown', handleKeyDown );
    };
  }, [ isOpen, noConfirmationModal, onClose ] );

  if ( !isOpen ) {
    return null;
  }

  const handleClose = () => {
    document.body.style.overflow = 'unset';
    setTimeout( () => {
      onClose();
    }, 0 );
  };

  const handleOverlayClick = ( e: React.MouseEvent<HTMLDivElement> ) => {
    if ( modalRef.current && !modalRef.current.contains( e.target as Node ) ) {
      e.preventDefault();
      e.stopPropagation();
      handleClose();
    }
  };

  const handleOverlayMouseUp = ( e: React.MouseEvent<HTMLDivElement> ) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <div
        className="fixed inset-0 flex h-screen w-screen items-center justify-center bg-black bg-opacity-30 z-50"
        onMouseDown={handleOverlayClick}
        onMouseUp={handleOverlayMouseUp}
      >
        <div
          ref={modalRef}
          className={`
            flex flex-col
            max-h-[95vh] h-fit min-w-[560px] w-8/12
            items-center justify-center
            rounded-xl bg-white-base drop-shadow-xl appear-top
            ${className}
          `}
          onMouseDown={e => { e.stopPropagation(); }}
          onMouseUp={e => { e.stopPropagation(); }}
        >
          <div className='flex flex-col h-fit w-full'>
            <TopModal icon={icon} title={title || ''} onClose={handleClose} noConfirmationModal={true} />
          </div>
          <div className='flex flex-col h-fit w-full overflow-y-auto overflow-x-visible'>
            { children }
          </div>
          <div className='flex flex-col h-fit w-full'>
            { footer }
          </div>
        </div>
      </div>
    </>
  );
};

export default BaseModal;
